import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Code as CodeIcon,
  Event as EventIcon,
  Save as SaveIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import BaseDialog from "../../../components/utils/dialog/BaseDialog";
import {
  datetimeLocalToIso,
  getDefaultPatchingDatetimeLocalToday,
  getPatchingWindowDatetimeLocal,
  isoToDatetimeLocalValue,
  PATCHING_WINDOW_PRESETS,
} from "./patchingScheduleDefaults";

const STATUS_OPTIONS = [
  { value: "planned", label: "Planned" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

function PatchingScheduleFormDialog({
  open,
  mode,
  schedule,
  templates = [],
  lockTemplateId,
  initialStartLocal,
  initialEndLocal,
  onClose,
  onSubmit,
  submitting,
}) {
  const isEdit = mode === "edit";
  const [templateId, setTemplateId] = useState("");
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");
  const [status, setStatus] = useState("planned");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTouched(false);
    const defaults = getDefaultPatchingDatetimeLocalToday();
    if (isEdit && schedule) {
      setTemplateId(schedule.template_id || "");
      setStartLocal(isoToDatetimeLocalValue(schedule.scheduled_start_time));
      setEndLocal(isoToDatetimeLocalValue(schedule.scheduled_end_time));
      setStatus(schedule.status || "planned");
    } else {
      setTemplateId(lockTemplateId || "");
      setStartLocal(initialStartLocal ?? defaults.startLocal);
      setEndLocal(initialEndLocal ?? defaults.endLocal);
      setStatus("planned");
    }
  }, [
    open,
    isEdit,
    schedule,
    lockTemplateId,
    initialStartLocal,
    initialEndLocal,
  ]);

  const handleSubmit = () => {
    if (submitting) return;
    setTouched(true);
    if (!templateId) return;
    const startIso = datetimeLocalToIso(startLocal);
    if (!startIso || invalidEnd) return;
    const payload = {
      template_id: templateId,
      scheduled_start_time: startIso,
      scheduled_end_time: datetimeLocalToIso(endLocal),
      status,
    };
    onSubmit({ s_id: isEdit ? schedule?.s_id : undefined, payload });
  };

  const startIso = datetimeLocalToIso(startLocal);
  const endIso = datetimeLocalToIso(endLocal);
  const invalidEnd =
    Boolean(endLocal) &&
    (!endIso || (startIso && new Date(endIso) <= new Date(startIso)));
  const endError = touched && invalidEnd;
  const startError = touched && !datetimeLocalToIso(startLocal);
  const templateError = touched && !templateId;
  const showTemplateSelect = isEdit || !lockTemplateId;
  const selectedWindow =
    PATCHING_WINDOW_PRESETS.find((preset) => {
      const patchingWindow = getPatchingWindowDatetimeLocal(
        startLocal.slice(0, 10),
        preset.value
      );
      return (
        startLocal === patchingWindow.startLocal &&
        endLocal === patchingWindow.endLocal
      );
    })?.value || null;

  const applyWindowPreset = (_, preset) => {
    if (!preset) return;
    const dateLocal =
      startLocal.slice(0, 10) ||
      getDefaultPatchingDatetimeLocalToday().startLocal.slice(0, 10);
    const patchingWindow = getPatchingWindowDatetimeLocal(dateLocal, preset);
    setStartLocal(patchingWindow.startLocal);
    setEndLocal(patchingWindow.endLocal);
  };

  const changeWindowDateTime = (value, setValue, setOtherValue) => {
    setValue(value);
    if (!value) return;
    setOtherValue((current) =>
      current ? `${value.slice(0, 10)}${current.slice(10)}` : current
    );
  };

  // An edited schedule can reference a template outside the currently loaded library.
  const availableTemplates = templates.some((t) => t.t_id === templateId)
    ? templates
    : templateId
      ? [
          ...templates,
          {
            ...schedule?.template,
            t_id: templateId,
            template_code: schedule?.template?.template_code || templateId,
          },
        ]
      : templates;
  const selectedTemplate = availableTemplates.find(
    (t) => t.t_id === templateId
  );

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      closeDisabled={submitting}
      maxWidth='md'
      title={isEdit ? "Edit Notification" : "Create Notification"}
      subtitle={
        isEdit
          ? "Review the patching window and update its status."
          : "Choose a template and set your patching window."
      }
      icon={isEdit ? <SaveIcon /> : <SendIcon />}
      paperSx={{ maxWidth: 780 }}
      contentSx={{ px: { xs: 2, sm: 3 } }}
      actions={
        <>
          <Button
            onClick={onClose}
            disabled={submitting}
            color='inherit'
            sx={{ mr: "auto", color: "text.secondary", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? null : isEdit ? <SaveIcon /> : <SendIcon />}
            sx={{ textTransform: "none" }}
          >
            {submitting
              ? "Saving..."
              : isEdit
                ? "Save changes"
                : "Create notification"}
          </Button>
        </>
      }
    >
      <Stack spacing={2.5}>
        <Box>
          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <CodeIcon sx={{ fontSize: 20, color: "primary.main" }} />
            <Typography variant='subtitle1' fontWeight={600}>
              Notification template
            </Typography>
          </Stack>
          <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
            {showTemplateSelect ? (
              <FormControl fullWidth size='small' error={templateError}>
                <InputLabel id='patch-schedule-template-label'>
                  Patching template
                </InputLabel>
                <Select
                  labelId='patch-schedule-template-label'
                  label='Patching template'
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  disabled={isEdit || submitting}
                >
                  {availableTemplates.map((t) => (
                    <MenuItem
                      key={t.t_id}
                      value={t.t_id}
                      disabled={!isEdit && !t.is_active}
                    >
                      {t.template_code}
                      {t.version ? ` (v${t.version})` : ""}
                      {!t.is_active ? " - inactive" : ""}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {templateError
                    ? "Select a template"
                    : isEdit
                      ? "The template is fixed for this notification."
                      : "Select an active template for this notification."}
                </FormHelperText>
              </FormControl>
            ) : (
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                spacing={1}
              >
                <Typography
                  variant='body2'
                  fontWeight={600}
                  sx={{ overflowWrap: "anywhere" }}
                >
                  {selectedTemplate?.template_code || lockTemplateId}
                </Typography>
                <Chip size='small' label='Selected' color='primary' />
              </Stack>
            )}
            {selectedTemplate?.subject && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                }}
              >
                <Typography variant='caption' color='text.secondary'>
                  Notification subject
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ overflowWrap: "anywhere", mt: 0.25 }}
                >
                  {selectedTemplate.subject}
                </Typography>
              </Box>
            )}
            {!isEdit && !templates.some((t) => t.is_active) && (
              <Alert severity='info' sx={{ mt: 1.5 }}>
                Create or activate a template in the library to schedule a
                notification.
              </Alert>
            )}
          </Paper>
        </Box>
        <Box>
          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <EventIcon sx={{ fontSize: 20, color: "primary.main" }} />
            <Typography variant='subtitle1' fontWeight={600}>
              Patching window
            </Typography>
          </Stack>
          <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              color='primary'
              value={selectedWindow}
              onChange={applyWindowPreset}
              disabled={submitting}
              aria-label='Patching window preset'
              sx={{
                mb: 2,
                "& .MuiToggleButton-root": { textTransform: "none" },
              }}
            >
              {PATCHING_WINDOW_PRESETS.map((preset) => (
                <ToggleButton key={preset.value} value={preset.value}>
                  <Stack>
                    <Typography variant='body2' fontWeight={600}>
                      {preset.label}
                    </Typography>
                    <Typography variant='caption'>
                      {preset.startTime}–{preset.endTime}
                    </Typography>
                  </Stack>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
                mb: 1,
              }}
            >
              <TextField
                label='Scheduled start'
                type='datetime-local'
                value={startLocal}
                onChange={(e) =>
                  changeWindowDateTime(e.target.value, setStartLocal, setEndLocal)
                }
                fullWidth
                required
                size='small'
                disabled={submitting}
                error={Boolean(startError)}
                helperText={startError ? "Start time is required" : " "}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label='Scheduled end'
                type='datetime-local'
                value={endLocal}
                onChange={(e) =>
                  changeWindowDateTime(e.target.value, setEndLocal, setStartLocal)
                }
                fullWidth
                size='small'
                disabled={submitting}
                error={Boolean(endError)}
                helperText={
                  endError ? "End time must be after the start time" : " "
                }
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <FormControl fullWidth size='small'>
              <InputLabel id='patch-schedule-status-label'>Status</InputLabel>
              <Select
                labelId='patch-schedule-status-label'
                label='Status'
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={submitting}
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Track the progress of this patching window.
              </FormHelperText>
            </FormControl>
          </Paper>
        </Box>
      </Stack>
    </BaseDialog>
  );
}

export default PatchingScheduleFormDialog;
